import { useEffect, useMemo, useState } from 'react';
import { costBasis, assetEquity, appreciation } from '@heivol-finance/calc';
import {
  assetsApi,
  loansApi,
  type AssetsData,
  type AssetRow,
  type AssetCostRow,
  type AssetType,
  type AssetCostStatus,
  type LoansData,
  type LoanRow,
  type LoanLedgerEntry,
} from '../lib/data.js';
import { formatOre, formatPercent } from '../lib/format.js';
import { loanOutstanding } from '../lib/loan.js';
import {
  PageHeader,
  CollapsibleCard,
  StatCard,
  Button,
  DeleteButton,
  TextField,
  OreField,
  NumberField,
  Select,
  EmptyState,
} from '../components/ui.js';
import { SectionBar, Tr, Th, Td } from '../components/table.js';

const TYPE_OPTS: { value: AssetType; label: string }[] = [
  { value: 'property', label: 'Property' },
  { value: 'vehicle', label: 'Vehicle' },
  { value: 'collectible', label: 'Collectible' },
  { value: 'other', label: 'Other' },
];
const typeLabel = (t: AssetType) => TYPE_OPTS.find((o) => o.value === t)?.label ?? t;

const STATUS_OPTS: { value: AssetCostStatus; label: string }[] = [
  { value: 'planned', label: 'Planned' },
  { value: 'verified', label: 'Verified' },
  { value: 'paid', label: 'Paid' },
];
const statusLabel = (s: AssetCostStatus) => STATUS_OPTS.find((o) => o.value === s)?.label ?? s;
// planned = coral/chat (not committed), verified = ai/violet (confirmed price), paid = success/green (spent).
const statusTone: Record<AssetCostStatus, string> = {
  planned: 'bg-hv-chat-surface text-hv-chat-text',
  verified: 'bg-hv-ai-surface text-hv-ai-text',
  paid: 'bg-hv-success-surface text-hv-success-text',
};

export function Assets(): JSX.Element {
  const [data, setData] = useState<AssetsData | null>(null);
  const [loans, setLoans] = useState<LoansData | null>(null);
  const reload = () => assetsApi.load().then(setData);
  useEffect(() => {
    void reload();
    void loansApi.load().then(setLoans);
  }, []);

  // Outstanding balance per loan id (formal principal / informal running-balance magnitude).
  const outstandingByLoan = useMemo(() => {
    const map = new Map<string, number>();
    if (!loans) return map;
    const entriesByLoan = new Map<string, LoanLedgerEntry[]>();
    for (const e of loans.entries) {
      const list = entriesByLoan.get(e.loan_id) ?? [];
      list.push(e);
      entriesByLoan.set(e.loan_id, list);
    }
    for (const loan of loans.loans) {
      map.set(loan.id, loanOutstanding(loan, entriesByLoan.get(loan.id) ?? []));
    }
    return map;
  }, [loans]);

  const loansById = useMemo(() => {
    const map = new Map<string, LoanRow>();
    for (const loan of loans?.loans ?? []) map.set(loan.id, loan);
    return map;
  }, [loans]);

  const loanOpts = useMemo<{ value: string; label: string }[]>(
    () => [
      { value: '', label: 'No loan' },
      ...(loans?.loans ?? []).map((l) => ({ value: l.id, label: l.name })),
    ],
    [loans],
  );

  const costsByAsset = useMemo(() => {
    const map = new Map<string, AssetCostRow[]>();
    for (const cost of data?.costs ?? []) {
      const list = map.get(cost.asset_id) ?? [];
      list.push(cost);
      map.set(cost.asset_id, list);
    }
    return map;
  }, [data]);

  // Portfolio totals: total market value + total equity (value − linked-loan outstanding).
  const totals = useMemo(() => {
    let value = 0;
    let equity = 0;
    for (const a of data?.assets ?? []) {
      const outstanding = a.linked_loan_id ? (outstandingByLoan.get(a.linked_loan_id) ?? 0) : 0;
      value += a.current_value_ore;
      equity += assetEquity(a.current_value_ore, outstanding);
    }
    return { value, equity };
  }, [data, outstandingByLoan]);

  if (!data) return <PageHeader title="Assets" subtitle="Loading…" />;

  return (
    <div className="max-w-5xl">
      <PageHeader
        title="Assets"
        subtitle={
          <>
            Things you own that rise or fall in value — the house, the Charger, Græstoftevej. Cost basis
            (purchase price + renovations) against market value, with linked loan and equity.
          </>
        }
      />

      <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
        <StatCard label="Total asset value" value={formatOre(totals.value)} tone="ai" hint="Total market value" />
        <StatCard
          label="Total equity"
          value={formatOre(totals.equity)}
          tone={totals.equity < 0 ? 'chat' : 'success'}
          hint="Market value − linked loans"
        />
        <StatCard label="Number of assets" value={String(data.assets.length)} />
      </div>

      <div className="mb-4 flex justify-end">
        <Button
          variant="secondary"
          onClick={() =>
            assetsApi
              .addAsset({ name: 'New asset', type: 'property', purchase_price_ore: 0, current_value_ore: 0, appreciation_rate: 0.02 })
              .then(reload)
          }
        >
          + Asset
        </Button>
      </div>

      {data.assets.length === 0 ? (
        <EmptyState>
          No assets yet. Add a property, a vehicle or a collectible, and record renovations as
          cost lines.
        </EmptyState>
      ) : (
        <div className="space-y-6">
          {data.assets.map((asset) => (
            <AssetCard
              key={asset.id}
              asset={asset}
              costs={costsByAsset.get(asset.id) ?? []}
              linkedLoan={asset.linked_loan_id ? (loansById.get(asset.linked_loan_id) ?? null) : null}
              linkedOutstanding={asset.linked_loan_id ? (outstandingByLoan.get(asset.linked_loan_id) ?? 0) : 0}
              loanOpts={loanOpts}
              reload={reload}
            />
          ))}
        </div>
      )}

      <p className="mt-6 text-hv-xs text-hv-text-tertiary">
        Cost lines (renovations, parts) are <span className="text-hv-text-secondary">asset-converting</span> — they raise the
        cost basis, not consumption. Net worth counts the market value as an asset and the linked loan as debt; equity =
        market value − outstanding balance.
      </p>
    </div>
  );
}

function AssetCard({
  asset,
  costs,
  linkedLoan,
  linkedOutstanding,
  loanOpts,
  reload,
}: {
  asset: AssetRow;
  costs: AssetCostRow[];
  linkedLoan: LoanRow | null;
  linkedOutstanding: number;
  loanOpts: { value: string; label: string }[];
  reload: () => Promise<void>;
}): JSX.Element {
  const patch = (b: Partial<AssetRow>) => assetsApi.updateAsset(asset.id, b).then(reload);

  const basis = useMemo(
    () => costBasis(asset.purchase_price_ore, costs.map((c) => c.amount_ore)),
    [asset.purchase_price_ore, costs],
  );
  const equity = assetEquity(asset.current_value_ore, linkedOutstanding);
  const gain = appreciation(asset.current_value_ore, basis);
  const gainPct = basis > 0 ? gain / basis : 0;

  const today = new Date().toISOString().slice(0, 10);

  return (
    <CollapsibleCard
      summary={
        <div className="flex items-center gap-2">
          <span>{asset.name}</span>
          <span className="rounded-hv-md bg-hv-ai-surface px-2 py-0.5 text-hv-xs text-hv-ai-text">{typeLabel(asset.type)}</span>
          <span className="ml-auto flex items-center gap-3 pl-2 tabular-nums">
            <span className="text-hv-text-secondary">{formatOre(asset.current_value_ore)}</span>
            <span className={`text-hv-xs ${equity < 0 ? 'text-hv-chat-text' : 'text-hv-success-text'}`}>
              Equity {formatOre(equity)}
            </span>
          </span>
        </div>
      }
      actions={<DeleteButton onClick={() => assetsApi.deleteAsset(asset.id).then(reload)} label="Delete asset" />}
    >
      {/* Parameters */}
      <div className="grid grid-cols-2 gap-4 border-b border-hv-border p-4 sm:grid-cols-3 lg:grid-cols-6">
        <Labeled label="Name">
          <TextField value={asset.name} onCommit={(v) => patch({ name: v })} />
        </Labeled>
        <Labeled label="Asset type">
          <Select value={asset.type} options={TYPE_OPTS} onChange={(v) => patch({ type: v })} />
        </Labeled>
        <Labeled label="Purchase price">
          <OreField value={asset.purchase_price_ore} onCommit={(v) => patch({ purchase_price_ore: v })} />
        </Labeled>
        <Labeled label="Market value">
          <OreField value={asset.current_value_ore} onCommit={(v) => patch({ current_value_ore: v })} />
        </Labeled>
        <Labeled label="Appreciation (% p.a.)">
          <NumberField
            value={asset.appreciation_rate * 100}
            step={0.1}
            suffix="%"
            onCommit={(v) => patch({ appreciation_rate: v / 100 })}
          />
        </Labeled>
        <Labeled label="Linked loan">
          <Select
            value={asset.linked_loan_id ?? ''}
            options={loanOpts}
            onChange={(v) => patch({ linked_loan_id: v === '' ? null : v })}
          />
        </Labeled>
      </div>

      {/* Derived stats */}
      <div className="grid grid-cols-2 gap-4 border-b border-hv-border p-4 sm:grid-cols-3 lg:grid-cols-5">
        <StatCard label="Market value" value={formatOre(asset.current_value_ore)} tone="ai" />
        <StatCard label="Cost basis" value={formatOre(basis)} hint="Purchase price + cost lines" />
        <StatCard
          label="Linked loan"
          value={linkedLoan ? formatOre(linkedOutstanding) : '—'}
          tone={linkedLoan ? 'chat' : 'default'}
          hint={linkedLoan ? `Outstanding · ${linkedLoan.name}` : 'No loan'}
        />
        <StatCard
          label="Equity"
          value={formatOre(equity)}
          tone={equity < 0 ? 'chat' : 'success'}
          hint="Market value − outstanding balance"
        />
        <StatCard
          label="Appreciation"
          value={formatOre(gain)}
          tone={gain < 0 ? 'warning' : 'success'}
          hint={`${gain < 0 ? '' : '+'}${formatPercent(gainPct)} vs. cost basis`}
        />
      </div>

      {/* Cost lines */}
      <SectionBar
        title="Cost lines (renovations / parts)"
        onAdd={() =>
          assetsApi.addCost(asset.id, { dated: today, amount_ore: 0, status: 'planned', note: '' }).then(reload)
        }
      />
      <div className="overflow-x-auto">
        <table className="w-full text-hv-sm">
          <thead>
            <Tr head>
              <Th>Date</Th>
              <Th>Note</Th>
              <Th>Status</Th>
              <Th right>Amount</Th>
              <Th />
            </Tr>
          </thead>
          <tbody>
            {costs.map((cost) => (
              <Tr key={cost.id}>
                <Td>
                  <TextField
                    value={cost.dated ?? ''}
                    placeholder="YYYY-MM-DD"
                    onCommit={(v) => assetsApi.updateCost(asset.id, cost.id, { dated: v }).then(reload)}
                  />
                </Td>
                <Td>
                  <TextField
                    value={cost.note ?? ''}
                    placeholder="e.g. New roof"
                    onCommit={(v) => assetsApi.updateCost(asset.id, cost.id, { note: v }).then(reload)}
                  />
                </Td>
                <Td>
                  <div className="flex items-center gap-2">
                    <span className={`rounded-hv-md px-2 py-0.5 text-hv-xs ${statusTone[cost.status]}`}>
                      {statusLabel(cost.status)}
                    </span>
                    <Select
                      value={cost.status}
                      options={STATUS_OPTS}
                      onChange={(v) => assetsApi.updateCost(asset.id, cost.id, { status: v }).then(reload)}
                      className="max-w-[9rem]"
                    />
                  </div>
                </Td>
                <Td right>
                  <OreField
                    value={cost.amount_ore}
                    onCommit={(v) => assetsApi.updateCost(asset.id, cost.id, { amount_ore: v }).then(reload)}
                  />
                </Td>
                <Td>
                  <DeleteButton onClick={() => assetsApi.deleteCost(asset.id, cost.id).then(reload)} />
                </Td>
              </Tr>
            ))}
            {costs.length === 0 && (
              <Tr>
                <Td colSpan={5}>
                  <span className="text-hv-text-tertiary">
                    No cost lines. Add renovations (construction, demolition, kitchen, roof) or parts — each with an
                    amount and a status (planned / verified / paid).
                  </span>
                </Td>
              </Tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="border-t border-hv-border px-4 py-2 text-hv-xs text-hv-text-tertiary">
        Cost lines raise the cost basis ({formatOre(basis)}); the market value is managed separately. Status: planned (not ordered),
        verified (price confirmed), paid (incurred).
      </div>
    </CollapsibleCard>
  );
}

function Labeled({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <label className="block">
      <span className="mb-1 block text-hv-xs uppercase tracking-wide text-hv-text-tertiary">{label}</span>
      {children}
    </label>
  );
}
